import type { LoanReceiptRef } from '@/features/loans/hooks';

/**
 * Per-payment record persisted in localStorage. Holds both the
 * "paid on" date and an optional receipt pointer (the receipt blob
 * itself lives in Supabase Storage; this is just the metadata
 * needed to render + delete it).
 *
 * Shared between the per-loan ledger (which writes) and the Debt
 * Balances rollup (which only reads) so both agree on which
 * scheduled payments have been settled.
 */
export interface PaymentRecord {
  /** ISO 'YYYY-MM-DD' of when the operator settled the payment. */
  paidOn?: string;
  /** Receipt blob pointer (Supabase Storage). */
  receipt?: LoanReceiptRef;
}

export type PaymentRecords = Record<number, PaymentRecord>;

const RECORDS_PREFIX = 'atlas:loans:records:';
/** Legacy storage key from before receipts were added — values were
 *  `Record<number, paidOnDate>`. We migrate transparently on first
 *  read so existing operators don't lose any dates they'd entered. */
const LEGACY_PAID_PREFIX = 'atlas:loans:paid:';

export function loanRecordsStorageKey(slug: string): string {
  return RECORDS_PREFIX + slug;
}

export function readRecordsFromStorage(slug: string): PaymentRecords {
  if (typeof window === 'undefined') return {};

  // Preferred path: new key with the full PaymentRecord shape.
  const fromNew = tryParseRecords(window.localStorage.getItem(RECORDS_PREFIX + slug));

  // One-time migration from the legacy `paid:` key. We merge so that
  // anything already saved under the new key wins (defensive: in case
  // a user has both keys present from racing with an older tab).
  const fromLegacy = tryParseLegacyPaid(
    window.localStorage.getItem(LEGACY_PAID_PREFIX + slug),
  );

  const merged: PaymentRecords = { ...fromLegacy, ...fromNew };
  for (const k of Object.keys(merged)) {
    const n = Number(k);
    if (fromNew[n] && fromLegacy[n]) {
      // New key always wins where both exist.
      merged[n] = fromNew[n];
    }
  }

  // If we did a migration write, clean up the legacy key so it doesn't
  // re-merge on every load. Wrapped in try/catch because removeItem
  // can throw in private mode / quota-exceeded scenarios.
  if (Object.keys(fromLegacy).length > 0) {
    try {
      window.localStorage.setItem(RECORDS_PREFIX + slug, JSON.stringify(merged));
      window.localStorage.removeItem(LEGACY_PAID_PREFIX + slug);
    } catch {
      /* in-memory state is still authoritative; we'll retry on next mutation. */
    }
  }

  return merged;
}

function tryParseRecords(raw: string | null): PaymentRecords {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: PaymentRecords = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = Number.parseInt(k, 10);
      if (!Number.isFinite(n)) continue;
      if (!v || typeof v !== 'object') continue;
      const record = v as Record<string, unknown>;
      const rec: PaymentRecord = {};
      if (typeof record.paidOn === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(record.paidOn)) {
        rec.paidOn = record.paidOn;
      }
      if (record.receipt && typeof record.receipt === 'object') {
        const r = record.receipt as Record<string, unknown>;
        if (
          typeof r.path === 'string' &&
          typeof r.filename === 'string' &&
          typeof r.uploadedAt === 'string'
        ) {
          rec.receipt = {
            path: r.path,
            filename: r.filename,
            uploadedAt: r.uploadedAt,
            size: typeof r.size === 'number' ? r.size : 0,
            contentType: typeof r.contentType === 'string' ? r.contentType : null,
          };
        }
      }
      if (rec.paidOn || rec.receipt) out[n] = rec;
    }
    return out;
  } catch {
    return {};
  }
}

function tryParseLegacyPaid(raw: string | null): PaymentRecords {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: PaymentRecords = {};
    for (const [k, v] of Object.entries(parsed)) {
      const n = Number.parseInt(k, 10);
      if (!Number.isFinite(n)) continue;
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        out[n] = { paidOn: v };
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function writeRecordsToStorage(slug: string, records: PaymentRecords) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RECORDS_PREFIX + slug, JSON.stringify(records));
  } catch {
    /* quota or private mode — in-memory state is still authoritative. */
  }
}
